import { assign, createMachine } from "xstate";
import {
  fetchCountries,
  IRoamingInternationalCountry,
  IZone,
} from "@nju/data-access-portal-dashboard";
import { fetchOffers } from "@nju/data-access-portal-offers";
import { createProductOrder } from "@nju/data-access-portal-order";
import { info, IPaymentMethod } from "@nju/data-access-portal-user";
import {
  pushCheckoutAdditionalPackageEvent,
  pushPurchaseAdditionalPackageEvent,
  pushSimpleEvent,
} from "@nju/data-layer";
import { parseSecondsToMinutes } from "@nju/utils";
import { mappedZoneToNameEvents } from "../../shared";

interface IContext {
  userId: string | undefined;
  productId: string;
  subId: string | undefined;
  userFunds: number;
  anniversaryDate: number | undefined;
  productOfferGrantValue: string;
  productOfferPrice: number;
  zone: IZone["name"] | undefined;
  countries: IRoamingInternationalCountry[];
  country: string | undefined;
  primaryPaymentMethod: IPaymentMethod["methodType"] | undefined;
}

type IEvent =
  | { type: "ERROR" }
  | { type: "SUCCESS" }
  | {
      type: "DONE";
      productId: string;
      userFunds: number;
      anniversaryDate: number | undefined;
      productOfferGrantValue: string;
      productOfferPrice: number;
      countries: IRoamingInternationalCountry[];
      primaryPaymentMethod: IPaymentMethod["methodType"];
    }
  | {
      type: "ADD_PACKAGE";
    }
  | {
      type: "ORDER_IN_PROGRESS";
    };

export const internationalSummaryMachine = createMachine(
  {
    context: {
      userId: undefined,
      zone: undefined,
      productId: "",
      subId: undefined,
      userFunds: 0,
      productOfferGrantValue: "",
      productOfferPrice: 0,
      anniversaryDate: undefined,
      primaryPaymentMethod: undefined,
      countries: [],
      country: undefined,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchData",
        },
        on: {
          DONE: [
            {
              cond: "isNotEnoughFunds",
              actions: ["setData", "pushDataLayer"],
              target: "#ready.noFunds",
            },
            {
              actions: "setData",
              target: "ready",
            },
          ],
          ERROR: {
            target: "error",
          },
        },
      },
      ready: {
        id: "ready",
        initial: "init",
        states: {
          init: {
            on: {
              ADD_PACKAGE: {
                target: "buying",
              },
            },
          },
          buying: {
            invoke: {
              src: "createOrder",
            },
            on: {
              SUCCESS: {
                target: "#success",
              },
              ERROR: {
                target: "#error",
              },
              ORDER_IN_PROGRESS: {
                target: "orderInProgress",
              },
            },
          },
          noFunds: {
            type: "final",
          },
          orderInProgress: {
            type: "final",
          },
        },
      },
      error: {
        id: "error",
      },
      success: {
        id: "success",
      },
    },
  },
  {
    guards: {
      isNotEnoughFunds: (_, event) => {
        if (event.type === "DONE") {
          return (
            event.primaryPaymentMethod === "DIGITAL_WALLET" &&
            event.userFunds < event.productOfferPrice
          );
        }

        return false;
      },
    },
    actions: {
      pushDataLayer: assign<IContext, IEvent>((context) => {
        pushSimpleEvent({
          event: "activity_service",
          eventName: "NotEnoughFunds",
        });

        return context;
      }),
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "DONE") {
          return {
            productId: event.productId,
            userFunds: event.userFunds,
            anniversaryDate: event.anniversaryDate,
            productOfferGrantValue: event.productOfferGrantValue,
            productOfferPrice: event.productOfferPrice,
            primaryPaymentMethod: event.primaryPaymentMethod,
          };
        }

        return context;
      }),
    },
    services: {
      fetchData: ({ zone, country }) => {
        return async (callback) => {
          try {
            const user = (await info()).unwrap();
            const offers = (await fetchOffers()).unwrap();
            const countries = (
              await fetchCountries({ brand: "NJU", category: "INTERNATIONAL" })
            ).unwrap();

            const countryResult = countries.find((item) => {
              return country === item.displayName.pl;
            });

            const product = offers.internationalCallsPackages?.find(
              (intPackage) => {
                return intPackage.zone === zone;
              },
            );

            const productId = product?.id;
            const productOfferPrice =
              product?.productOfferingPrice?.price.amount || 0;
            const productOfferGrantValue = product?.balances?.[0]?.grantValue;
            const primaryPaymentMethod = user.primaryPaymentMethod?.methodType;
            const userFunds = user.currentFunds;
            const anniversaryDate = product?.refillPeriod;
            const variant = zone ? mappedZoneToNameEvents[zone] : "";

            pushCheckoutAdditionalPackageEvent({
              products: [
                {
                  brand: "nju",
                  quantity: 1,
                  category: "VAS/Rozmowy Międzynarodowe",
                  variant,
                  price: String(productOfferPrice),
                  name: `Pakiet rozmów międzynarodowych ${parseSecondsToMinutes(
                    productOfferGrantValue,
                  )} min./SMS/MMS`,
                  id: String(productId),
                },
              ],
            });

            if (
              productId === undefined ||
              productOfferPrice === undefined ||
              productOfferGrantValue === undefined ||
              !countryResult ||
              primaryPaymentMethod === undefined
            ) {
              return callback({ type: "ERROR" });
            }

            return callback({
              type: "DONE",
              anniversaryDate,
              primaryPaymentMethod,
              productId,
              productOfferGrantValue,
              productOfferPrice,
              userFunds,
              countries,
            });
          } catch {
            callback({ type: "ERROR" });
          }
        };
      },
      createOrder: ({
        productId,
        userId,
        subId,
        zone,
        productOfferPrice,
        productOfferGrantValue,
      }) => {
        return async (callback) => {
          const variant = zone ? mappedZoneToNameEvents[zone] : "";

          pushPurchaseAdditionalPackageEvent({
            products: [
              {
                brand: "nju",
                quantity: 1,
                category: "VAS/Rozmowy Międzynarodowe",
                variant,
                price: String(productOfferPrice),
                name: `Pakiet rozmów międzynarodowych ${parseSecondsToMinutes(
                  productOfferGrantValue,
                )} min./SMS/MMS`,
                id: String(productId),
              },
            ],
          });

          const result = await createProductOrder({
            targetUserId: userId,
            productId,
            subId,
          });

          if (result.isError) {
            if (result.err.code === "409_CONFLICT") {
              pushSimpleEvent({
                event: "activity_service",
                eventName: "AnotherOrderInProgess",
              });

              return callback({ type: "ORDER_IN_PROGRESS" });
            }
            pushSimpleEvent({
              event: "activity_service",
              eventName: "PaymentFailed",
            });

            return callback({ type: "ERROR" });
          }

          return callback({ type: "SUCCESS" });
        };
      },
    },
  },
);
